import { useState } from 'react'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'

/**
 * The card-on-file mandate fields — Stripe's hosted card form plus the confirm button,
 * and the plain-English error wording that goes with it.
 *
 * ONE copy, used by BOTH places a card mandate can be captured:
 *   • the standalone page  /setup-card/<token>        (pages/website/SetupCard.tsx)
 *   • inline on the contract signing page             (pages/website/SignContract.tsx)
 * — exactly as the Direct Debit mandate is captured in both places. Keeping it here
 * means what the customer sees and agrees to cannot drift between the two routes.
 *
 * ⚠️ Must be rendered inside an <Elements> provider built from the shared getStripe()
 * loader (components/payment/StripePayment.tsx). Never add a second loadStripe().
 * ⚠️ No card number ever reaches this component — it only exists inside Stripe's iframe.
 */

export function ordinal(n: number): string {
  const v = Math.abs(Math.round(n)) % 100
  const suffix = v >= 11 && v <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][v % 10] || 'th'
  return `${n}${suffix}`
}

export function money(n: number | null | undefined): string {
  return `£${Number(n || 0).toFixed(2)}`
}

export function prettyDate(d?: string | null): string {
  if (!d) return ''
  const parsed = new Date(d)
  if (isNaN(parsed.getTime())) return String(d)
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

/** Turn a Stripe error into something a customer can actually act on. */
export function friendlyCardError(err: any): string {
  const code = err?.decline_code || err?.code || ''
  switch (code) {
    case 'insufficient_funds':
      return 'Your bank turned down the check payment because of the funds available on the card. Please try a different card.'
    case 'expired_card':
      return 'That card has expired. Please try a different card.'
    case 'incorrect_cvc':
    case 'invalid_cvc':
      return 'The security code (the 3 digits on the back of the card) did not match. Please check it and try again.'
    case 'incorrect_number':
    case 'invalid_number':
      return 'That card number was not recognised. Please check it and try again.'
    case 'card_not_supported':
      return 'That card cannot be used for monthly payments. Please try a different card — a debit card usually works best.'
    case 'payment_intent_authentication_failure':
      return 'The security check with your bank was not completed, so nothing has been set up. You can try again, or use a different card.'
    case 'card_declined':
    case 'generic_decline':
      return 'Your bank declined the card. They do not tell us why — it is often a routine security block. Please try again, try a different card, or check with your bank.'
    default:
      return (
        err?.message ||
        'We could not set the card up. Nothing has been charged. Please try again or use a different card.'
      )
  }
}

/** The Stripe-hosted card fields plus the confirm button. Must live inside <Elements>. */
export function CardEntry({
  amountLabel,
  consent,
  submitting,
  setSubmitting,
  formError,
  setFormError,
  onAuthorised,
  onTryAnother,
}: {
  amountLabel: string
  consent: boolean
  submitting: boolean
  setSubmitting: (v: boolean) => void
  formError: string
  setFormError: (v: string) => void
  onAuthorised: (paymentIntentId: string) => Promise<void>
  onTryAnother: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [declined, setDeclined] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements || !consent || submitting) return
    setSubmitting(true)
    setFormError('')
    setDeclined(false)
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        // If the bank insists on a full-page 3DS redirect we come straight back
        // to this page and finish there.
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })

      if (error) {
        setFormError(friendlyCardError(error))
        setDeclined(true)
        return
      }
      if (!paymentIntent) {
        setFormError('We did not get a response from your bank. Nothing has been set up — please try again.')
        setDeclined(true)
        return
      }
      if (paymentIntent.status === 'requires_payment_method') {
        // The customer backed out of the bank's security check.
        setFormError('The security check with your bank was not completed, so nothing has been set up. Please try again, or use a different card.')
        setDeclined(true)
        return
      }
      // 'succeeded' or 'processing' — the card is authorised; the backend is the
      // judge of whether it can be stored.
      await onAuthorised(paymentIntent.id)
    } catch (err: any) {
      setFormError(
        err?.response?.data?.detail ||
        err?.message ||
        'Something went wrong setting the card up. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-3 bg-white rounded-lg border">
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
            wallets: { applePay: 'never', googlePay: 'never' },
          }}
        />
      </div>

      {formError && (
        <Alert variant="error" className="mt-3">{formError}</Alert>
      )}

      {declined ? (
        <Button variant="primary" className="w-full mt-4" onClick={onTryAnother} type="button">
          Try another card
        </Button>
      ) : (
        <Button
          variant="primary"
          className="w-full mt-4"
          type="submit"
          isLoading={submitting}
          disabled={!stripe || !consent || submitting}
        >
          Confirm card &amp; take {amountLabel} check payment
        </Button>
      )}

      {!consent && !declined && (
        <p className="text-xs text-gray-500 mt-2 text-center">
          Please tick the box above to confirm you are happy with the monthly payment.
        </p>
      )}
    </form>
  )
}
